import * as React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/app/components/ui/button";
import { Label } from "@/app/components/ui/label";
import { Select } from "@/app/components/ui/select";

export const PAGE_SIZE_OPTIONS = [5, 10, 20] as const;
export const DEFAULT_PAGE_SIZE = 5;

export function usePagedItems<T>(items: T[], pageSize: number) {
  const [page, setPage] = React.useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  React.useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);
  const pageItems = items.slice((page - 1) * pageSize, page * pageSize);
  return { page, setPage, totalPages, pageItems };
}

function visiblePageItems(page: number, totalPages: number) {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  if (page <= 3) return [1, 2, 3, "ellipsis", totalPages] as const;
  if (page >= totalPages - 2) {
    return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages] as const;
  }
  return [1, "ellipsis-start", page, "ellipsis-end", totalPages] as const;
}

export function DataPagination({
  page,
  totalPages,
  pageSize,
  onChange,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
}: {
  page: number;
  totalPages: number;
  pageSize: number;
  onChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: readonly number[];
}) {
  const { t } = useTranslation();
  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border px-1 pt-2 text-xs">
      <Label className="flex items-center gap-2 text-xs text-muted-foreground">
        <Select
          value={String(pageSize)}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="h-8 w-20 text-xs"
          aria-label={t("pagination.itemsPerPage")}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </Select>
        {t("pagination.itemsPerPage")}
      </Label>
      <div className="flex items-center justify-end gap-1">
        <Button
          size="sm"
          variant="outline"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          aria-label={t("pagination.prev")}
        >
          {"<"}
        </Button>
        {visiblePageItems(page, totalPages).map((item, index) =>
          typeof item === "number" ? (
            <Button
              key={item}
              size="sm"
              variant={item === page ? "default" : "outline"}
              onClick={() => onChange(item)}
              aria-current={item === page ? "page" : undefined}
              className="min-w-8 px-2"
            >
              {item}
            </Button>
          ) : (
            <span
              key={`${item}-${index}`}
              className="px-1 text-muted-foreground"
              aria-hidden="true"
            >
              ...
            </span>
          )
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
          aria-label={t("pagination.next")}
        >
          {">"}
        </Button>
      </div>
    </div>
  );
}
